import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  Users, Search, Send, Phone, Mail, CheckCircle2, Clock, AlertTriangle, CircleSlash,
  BedDouble, GraduationCap, Wallet, Plus, Minus, FileDown, ExternalLink,
} from "lucide-react";

const STATUS_MAP = {
  paid:       { label: "Paid",       cls: "bg-green-500/15 text-green-300 border-green-500/50",  Icon: CheckCircle2 },
  pending:    { label: "Pending",    cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/50", Icon: Clock },
  overdue:    { label: "Overdue",    cls: "bg-red-500/15 text-red-300 border-red-500/50",         Icon: AlertTriangle },
  not_billed: { label: "Not billed", cls: "bg-white/5 text-white/40 border-white/15",             Icon: CircleSlash },
};

/**
 * Ward list — shared page used by both academic and hostel routes.
 * `kind` = 'academic' | 'hostel' controls filter + extra columns/actions.
 */
export default function AdminWards({ kind = "academic" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("pending");
  const [pinging, setPinging] = useState({});
  const [sharingPdf, setSharingPdf] = useState({});
  const [walletModal, setWalletModal] = useState(null);  // {ward_id, ward_name, type}

  const isHostel = kind === "hostel";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/wards-summary?kind=${kind}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "pending"   && r.pending_total <= 0) return false;
      if (filter === "active"    && !r.active_enrollment) return false;
      if (filter === "withdrawn" &&  r.active_enrollment) return false;
      if (!q) return true;
      return (r.name || "").toLowerCase().includes(q)
          || (r.parent_name || "").toLowerCase().includes(q)
          || (r.parent_phone || "").includes(q)
          || (r.sport || "").toLowerCase().includes(q)
          || (r.batch_name || "").toLowerCase().includes(q);
    });
  }, [rows, query, filter]);

  const totals = useMemo(() => filtered.reduce((acc, r) => {
    acc.pending += r.pending_total || 0;
    acc.regPaid += r.registration_paid || 0;
    acc.admPaid += r.admission_paid || 0;
    acc.secPaid += r.security_paid || 0;
    acc.wallet += r.wallet_balance || 0;
    return acc;
  }, { pending: 0, regPaid: 0, admPaid: 0, secPaid: 0, wallet: 0 }), [filtered]);

  const remind = async (wardId) => {
    setPinging((p) => ({ ...p, [wardId]: true }));
    try {
      const { data } = await api.post(`/admin/wards/${wardId}/remind-fees`);
      toast.success(`Sent to ${data.sent_to} · ${data.pending_invoices} pending · ₹${data.total_pending}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPinging((p) => ({ ...p, [wardId]: false })); }
  };

  const sharePdf = async (wardId) => {
    setSharingPdf((s) => ({ ...s, [wardId]: true }));
    try {
      const { data } = await api.post(`/admin/wards/${wardId}/wallet/share-pdf`);
      toast.success(`Wallet PDF link sent to ${data.sent_to} · Balance ₹${data.balance}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSharingPdf((s) => ({ ...s, [wardId]: false })); }
  };

  const downloadPdf = (wardId) => {
    // Same endpoint that share-pdf uses — opens the PDF in a new tab
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3) + 1;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/wards/${wardId}/wallet/statement.pdf?quarter=${q}&year=${now.getFullYear()}`;
    window.open(url, "_blank");
  };

  const Icon = isHostel ? BedDouble : GraduationCap;
  const title = isHostel ? "Hostel Ward List." : "Academic Ward List.";
  const strap = isHostel
    ? "Every hostel-resident ward with fees paid, wallet balance and one-tap wallet controls. Top-up, log expenses and share the ledger PDF right from here."
    : "Every day-scholar ward with rolled-up amounts paid per fee bucket + this-month monthly-fee status. Tap Remind to WhatsApp the parent every outstanding line.";

  return (
    <div data-testid={`admin-wards-${kind}`}>
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Academy · Ward ledger · {isHostel ? "Hostel" : "Academic"}</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-2 flex items-center gap-3">
        <Icon className="brand-orange" size={36} /> {title}
      </h1>
      <p className="text-sm text-white/60 max-w-3xl mb-6">{strap}</p>

      <div className={`grid grid-cols-2 md:grid-cols-${isHostel ? "6" : "5"} gap-3 mb-4`}>
        <Stat label="Wards shown"       value={filtered.length} raw />
        <Stat label="Pending total"     value={totals.pending}  danger />
        <Stat label="Reg. collected"    value={totals.regPaid} />
        <Stat label="Adm. collected"    value={totals.admPaid} />
        <Stat label="Sec. dep. held"    value={totals.secPaid} />
        {isHostel && <Stat label="Wallet float" value={totals.wallet} tone="blue" />}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by ward, parent, phone, sport, batch…"
            data-testid="wards-search"
            className="w-full bg-[#131A2B] border border-white/15 py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:border-brand-orange" />
        </div>
        {["pending", "all", "active", "withdrawn"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            data-testid={`wards-filter-${f}`}
            className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest border ${filter === f ? "border-brand-orange bg-brand-orange text-black" : "border-white/15 hover:bg-white/5"}`}
          >{f}</button>
        ))}
      </div>

      {loading ? (
        <div className="border border-white/12 bg-[#131A2B] p-10 text-center text-white/40">Loading wards…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/15 bg-[#131A2B] p-10 text-center text-white/40 text-sm">
          No {isHostel ? "hostel-resident" : "academic day-scholar"} wards match this filter.
        </div>
      ) : (
        <div className="border border-white/12 bg-[#131A2B] overflow-x-auto" data-testid="wards-table-wrap">
          <table className="w-full text-sm min-w-[1200px]">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-widest text-white/40 border-b border-white/12">
                <th className="text-left p-3">Ward</th>
                <th className="text-left p-3">Parent · Contact</th>
                <th className="text-left p-3">Admission</th>
                <th className="text-right p-3">Reg.</th>
                <th className="text-right p-3">Adm.</th>
                <th className="text-right p-3">Sec.</th>
                <th className="text-center p-3">This-month</th>
                <th className="text-right p-3">Pending</th>
                {isHostel && <th className="text-right p-3">Wallet</th>}
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const st = STATUS_MAP[r.monthly_current] || STATUS_MAP.not_billed;
                const StIcon = st.Icon;
                return (
                  <tr key={r.id} className="border-b border-white/6 hover:bg-white/4" data-testid={`ward-row-${r.id}`}>
                    <td className="p-3">
                      <div className="font-bold">{r.name}</div>
                      <div className="text-[11px] text-white/50 mt-0.5">
                        {r.sport ? <span className="uppercase">{r.sport}</span> : "—"}
                        {r.batch_name && <span> · {r.batch_name}</span>}
                        {r.dormitory_bed && <span className="ml-1 brand-orange">· Room {r.dormitory_bed.room}/{r.dormitory_bed.bed}</span>}
                        {!r.active_enrollment && <span className="ml-2 text-red-300 uppercase text-[10px] font-mono">Withdrawn</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">{r.parent_name || "—"}</div>
                      <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-white/60">
                        {r.parent_phone && (
                          <a href={`tel:${r.parent_phone}`} className="inline-flex items-center gap-1 hover:text-white">
                            <Phone size={10} /> {r.parent_phone}
                          </a>
                        )}
                        {r.parent_email && (
                          <a href={`mailto:${r.parent_email}`} className="inline-flex items-center gap-1 hover:text-white">
                            <Mail size={10} /> {r.parent_email}
                          </a>
                        )}
                        {r.parent_id && (
                          <Link to={`/admin/parents?parent=${r.parent_id}`}
                            data-testid={`ward-parent-link-${r.id}`}
                            className="inline-flex items-center gap-1 brand-blue hover:text-white border-b border-brand-blue/40">
                            <ExternalLink size={10} /> View parent
                          </Link>
                        )}
                      </div>
                    </td>
                    <td className="p-3 font-mono text-[11px] text-white/60">{r.admission_date || "—"}</td>
                    <td className="p-3 text-right font-mono">{formatINR(r.registration_paid)}</td>
                    <td className="p-3 text-right font-mono">{formatINR(r.admission_paid)}</td>
                    <td className="p-3 text-right font-mono">{formatINR(r.security_paid)}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${st.cls}`}>
                        <StIcon size={11} /> {st.label}
                      </span>
                    </td>
                    <td className={`p-3 text-right font-mono font-bold ${r.pending_total > 0 ? "text-red-300" : "text-white/40"}`}>
                      {formatINR(r.pending_total)}
                      {r.pending_count > 0 && (
                        <div className="text-[10px] font-mono text-white/40 mt-0.5">{r.pending_count} invoice{r.pending_count === 1 ? "" : "s"}</div>
                      )}
                    </td>
                    {isHostel && (
                      <td className="p-3 text-right">
                        <div className={`font-mono font-bold ${r.wallet_balance < 0 ? "text-red-300" : "brand-blue"}`}>
                          {formatINR(r.wallet_balance)}
                        </div>
                        <CategoryBar spend={r.wallet_spend_by_category} />
                      </td>
                    )}
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                        <button onClick={() => remind(r.id)}
                          disabled={pinging[r.id] || r.pending_total <= 0 || !r.parent_phone}
                          data-testid={`ward-remind-${r.id}`}
                          title={r.pending_total <= 0 ? "No pending fees" : (!r.parent_phone ? "No WhatsApp number on file" : "Send WhatsApp reminder for all pending fees")}
                          className="inline-flex items-center gap-1 border border-brand-orange text-brand-orange px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-brand-orange hover:text-black disabled:opacity-30 disabled:cursor-not-allowed">
                          <Send size={10} /> {pinging[r.id] ? "…" : "Remind"}
                        </button>
                        {isHostel && (
                          <>
                            <button onClick={() => setWalletModal({ wardId: r.id, wardName: r.name, type: "credit" })}
                              data-testid={`ward-wallet-topup-${r.id}`}
                              className="inline-flex items-center gap-1 border border-green-500/50 text-green-300 px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-green-500 hover:text-black">
                              <Plus size={10} /> Top-up
                            </button>
                            <button onClick={() => setWalletModal({ wardId: r.id, wardName: r.name, type: "debit" })}
                              data-testid={`ward-wallet-expense-${r.id}`}
                              className="inline-flex items-center gap-1 border border-yellow-500/50 text-yellow-300 px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-yellow-500 hover:text-black">
                              <Minus size={10} /> Expense
                            </button>
                            <button onClick={() => downloadPdf(r.id)}
                              data-testid={`ward-wallet-download-${r.id}`}
                              className="inline-flex items-center gap-1 border border-white/25 text-white/70 px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-white/10">
                              <FileDown size={10} /> PDF
                            </button>
                            {r.parent_phone && (
                              <button onClick={() => sharePdf(r.id)} disabled={sharingPdf[r.id]}
                                data-testid={`ward-wallet-share-${r.id}`}
                                className="inline-flex items-center gap-1 border border-brand-blue text-brand-blue px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-brand-blue hover:text-white disabled:opacity-40">
                                <Send size={10} /> {sharingPdf[r.id] ? "…" : "Share PDF"}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {walletModal && (
        <QuickWalletModal {...walletModal} onClose={() => setWalletModal(null)} onDone={() => { setWalletModal(null); load(); }} />
      )}
    </div>
  );
}

function Stat({ label, value, danger, raw, tone }) {
  return (
    <div className={`border p-3 ${danger ? "border-red-500/40 bg-red-500/10" : "border-white/12 bg-[#131A2B]"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-display text-xl font-black mt-1 ${danger ? "text-red-300" : tone === "blue" ? "brand-blue" : ""}`}>
        {raw ? value : formatINR(value)}
      </div>
    </div>
  );
}

const CAT_COLORS = {
  kit:      "bg-brand-orange",
  medical:  "bg-red-500",
  personal: "bg-brand-blue",
  other:    "bg-slate-500",
};
const CAT_HEX = {
  kit:      "#F97316",
  medical:  "#DC2626",
  personal: "#2563EB",
  other:    "#6B7280",
};

/** Horizontal proportion bar showing where the wallet money went (kit/medical/personal/other). */
function CategoryBar({ spend }) {
  const s = spend || { kit: 0, medical: 0, personal: 0, other: 0 };
  const total = (s.kit || 0) + (s.medical || 0) + (s.personal || 0) + (s.other || 0);
  if (total <= 0) {
    return <div className="text-[9px] font-mono text-white/30 mt-1">No spend yet</div>;
  }
  const parts = ["kit", "medical", "personal", "other"].map((k) => ({ k, v: s[k] || 0, pct: (s[k] || 0) / total * 100 }));
  const legend = parts.filter((p) => p.v > 0);
  return (
    <div className="mt-1.5 min-w-[140px]">
      <div className="flex h-1.5 w-full border border-white/10 overflow-hidden">
        {parts.map((p) => p.v > 0 && (
          <div key={p.k} className={`${CAT_COLORS[p.k]}`} style={{ width: `${p.pct}%` }}
            title={`${p.k}: ₹${p.v.toLocaleString("en-IN")} (${p.pct.toFixed(0)}%)`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 justify-end">
        {legend.map((p) => (
          <span key={p.k} className="text-[9px] font-mono text-white/60 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5" style={{ background: CAT_HEX[p.k] }} />
            {p.k} {p.pct.toFixed(0)}%
          </span>
        ))}
      </div>
    </div>
  );
}

function QuickWalletModal({ wardId, wardName, type, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [method, setMethod] = useState("cash");
  const [txnRef, setTxnRef] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) return toast.error("Enter a valid amount");
    if (!desc.trim()) return toast.error("Add a short description");
    if (type === "credit" && method !== "cash" && !txnRef.trim()) return toast.error("Txn ref required for non-cash top-ups");
    setSaving(true);
    try {
      const body = { type, amount: parseFloat(amount), description: desc.trim(), category: "other" };
      if (type === "credit") { body.method = method; body.txn_ref = txnRef.trim() || null; }
      await api.post(`/admin/wards/${wardId}/wallet/entry`, body);
      toast.success(type === "credit"
        ? `Wallet topped up · ₹${amount} via ${method.toUpperCase()} · posted to Bank Book`
        : `Expense logged · ₹${amount}`);
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const isTopup = type === "credit";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#131A2B] border border-white/12 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}
        data-testid="quick-wallet-modal">
        <div className="mb-4">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">
            // {isTopup ? "Wallet · Desk top-up" : "Wallet · Log expense"}
          </div>
          <div className="font-display text-2xl font-black uppercase">{isTopup ? "Top-up wallet" : "Add expense"}</div>
          <div className="text-xs text-white/50 mt-0.5">Ward: <b className="text-white">{wardName}</b></div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Amount ₹</div>
            <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="quick-wallet-amount" autoFocus
              className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 font-mono focus:outline-none focus:border-brand-orange" />
          </label>
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Description</div>
            <input value={desc} onChange={(e) => setDesc(e.target.value)}
              data-testid="quick-wallet-desc"
              placeholder={isTopup ? "e.g. Quarter 2 wallet top-up" : "e.g. Books + stationery"}
              className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-orange" />
          </label>
          {isTopup && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Method</div>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                  data-testid="quick-wallet-method"
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank_transfer">Bank transfer</option>
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Txn / Ref</div>
                <input value={txnRef} onChange={(e) => setTxnRef(e.target.value)}
                  data-testid="quick-wallet-ref"
                  placeholder={method === "cash" ? "Optional" : "Required"}
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm font-mono focus:outline-none focus:border-brand-orange" />
              </label>
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t border-white/10">
            <button type="button" onClick={onClose} className="flex-1 border border-white/20 py-2.5 text-sm font-mono uppercase tracking-widest">Cancel</button>
            <button type="submit" disabled={saving}
              data-testid="quick-wallet-submit"
              className={`flex-1 py-2.5 text-sm font-bold uppercase tracking-widest disabled:opacity-50 ${isTopup ? "bg-green-500 text-black" : "bg-yellow-500 text-black"}`}>
              {saving ? "Saving…" : (isTopup ? "Top-up" : "Log expense")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
